import { type Property } from '@resolute/schema';

import { formatAreaWithUnit, type UnitTableColumns } from '../use-detail-facts';

/**
 * The "Available configurations" table — one row per unit type.
 *
 * Only rendered when a project has more than one unit type; with a single one
 * the summary above it carries the same fields. Which columns appear is decided
 * in `useDetailFacts` (see {@link UnitTableColumns}) so the page and this table
 * cannot disagree about it.
 */
export function UnitTypesTable({
  unitTypes,
  columns,
  typeLabel,
}: {
  unitTypes: NonNullable<Property['unitTypes']>;
  columns: UnitTableColumns;
  /** Resolves a raw type slug to its display label (from `useFilterLabels`). */
  typeLabel: (value: string) => string;
}) {
  const { showTypeColumn, showConfigColumn, showSuperBuiltUpColumn, showUnitsColumn } = columns;

  return (
    <section aria-labelledby="configurations-heading">
      <h2 id="configurations-heading" className="text-lg font-semibold">
        Available configurations
      </h2>
      <div className="mt-3 overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {showTypeColumn && <th className="px-4 py-3 font-medium">Property type</th>}
              {showConfigColumn && <th className="px-4 py-3 font-medium">Configuration</th>}
              <th className="px-4 py-3 font-medium">RERA area</th>
              {showSuperBuiltUpColumn && <th className="px-4 py-3 font-medium">Super built-up</th>}
              {showUnitsColumn && <th className="px-4 py-3 font-medium">Units</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {unitTypes.map((u, i) => {
              const unitArea = u.area.builtUp ?? u.area.carpet;
              return (
                <tr key={i}>
                  {showTypeColumn && (
                    <td className="px-4 py-3 font-medium">{typeLabel(u.type)}</td>
                  )}
                  {showConfigColumn && (
                    <td className="px-4 py-3 font-medium">{u.config ? `${u.config} BHK` : '—'}</td>
                  )}
                  <td className="px-4 py-3 text-muted-foreground">
                    {unitArea ? formatAreaWithUnit(unitArea, undefined, u.areaUnit) : '—'}
                  </td>
                  {showSuperBuiltUpColumn && (
                    <td className="px-4 py-3 text-muted-foreground">
                      {u.area.superBuiltUp
                        ? formatAreaWithUnit(u.area.superBuiltUp, undefined, u.areaUnit)
                        : '—'}
                    </td>
                  )}
                  {/* CPzone defaults an unset unit count to 0 (the
                      admin field is left blank), and a listing that
                      offers zero units is not a real statement —
                      so 0 reads as "not recorded". */}
                  {showUnitsColumn && (
                    <td className="px-4 py-3 text-muted-foreground">
                      {u.totalUnits ? u.totalUnits : '—'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
